import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import {
  router,
  useFocusEffect,
  useGlobalSearchParams,
  useLocalSearchParams,
  useNavigation,
  useRouter,
} from 'expo-router';
import { useHeaderHeight } from 'expo-router/react-navigation';
import { ChevronLeft, ChevronRight } from 'lucide-react-native';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Keyboard, LayoutChangeEvent, Platform, ScrollView, View } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useReducedMotion } from 'react-native-reanimated';
import { twJoin } from 'tailwind-merge';

import { CommunityPostContent } from './CommunityPostContent';
import { nativeCommentsScrollY, scrollCommentsIntoView } from './commentsScroll';
import { useCommunityPostReadTracking } from '../../hooks/useCommunityPostReadTracking';
import { useLibraryPage } from '../../hooks/useLibraryPage';
import { useLibraryPost } from '../../hooks/useLibraryPost';
import { useListedLibraryPost } from '../../hooks/useListedLibraryPost';
import { useRefreshPostAfterComment } from '../../hooks/useRefreshPostAfterComment';
import { postFormatOf, readInquiryLibraryAnswers } from '../../model/answers';
import type { LibraryListParams, LibraryPost } from '../../model/types';
import { useLibraryFilters } from '../../state/libraryFiltersStore';
import { CommunityActionBar } from '../actions/CommunityActionBar';
import { CommunityWrapper } from '../list/CommunityWrapper';
import { LibraryRetryNotice } from '../list/LibraryRetryNotice';
import { PostDetailSkeleton } from '../list/PostCardSkeleton';

import { Header } from '@/components/header';
import type { HeaderItem } from '@/components/headerItems/types';
import { useIsHeaderFloating } from '@/components/headerItems/useIsHeaderFloating';
import { RefreshControl } from '@/components/ui/refresh-control';
import { VStack } from '@/components/ui/vstack';
import { type SortOption } from '@/constants/community';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { ROUTES } from '@/constants/routes';
import {
  CommentBox,
  Comments,
  useComments,
  useCreateComment,
  type CommentBoxHandle,
} from '@/features/comments';
import {
  getCommentIsAnonymous,
  type AnyComment,
  type CommentAuthor,
} from '@/features/comments/model';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useToaster } from '@/hooks/useToaster';
import { useUserScope } from '@/hooks/useUserScope';
import {
  trackCommentSubmitted,
  trackCommunityPostStepped,
  trackCommunityPostViewed,
  type CommunityPostOpenedVia,
} from '@/util/analyticsEvents';
import { parseStringBoolean } from '@/util/parseStringBoolean';

const POSTS_PER_PAGE = 25;

/**
 * Resolves the posts either side of the one on screen, so Prev and Next walk the
 * same order the library list is in.
 *
 * Position is 1-based and comes from the list, so the page a post sits on is
 * arithmetic rather than a search. Both pages are read through
 * `useLibraryPage`, which caches them: walking a page of
 * posts resolves one fetch for all 25 instead of one per post. The adjacent page
 * is only requested at a page boundary, where the neighbour genuinely lives on
 * another page.
 */
const usePreviousAndNextPostIds = (postId: number, position?: string) => {
  const cancerTypeId = Number(useLibraryFilters((state) => state.cancerTypeId)) || undefined;

  const searchQuery = useLibraryFilters((state) => state.searchQuery) || undefined;

  const showSavedPosts = useLibraryFilters((state) => state.showSavedPosts) || undefined;

  const params = useLocalSearchParams<{
    sortBy?: string;
    'cancer-type-id'?: string;
    'saved-posts'?: 'true' | 'false';
    q?: string;
  }>();

  const urlCancerTypeId = params['cancer-type-id'] ? Number(params['cancer-type-id']) : undefined;

  const urlSavedPosts = parseStringBoolean(params['saved-posts']);

  const urlSearchQuery = params.q;

  const effectiveCancerTypeId = urlCancerTypeId ?? cancerTypeId;

  const effectiveSearchQuery = urlSearchQuery ?? searchQuery;

  const effectiveShowSavedPosts = urlSavedPosts ?? showSavedPosts;

  const sortBy = params.sortBy as SortOption | undefined;

  const normalizedSortBy: SortOption | undefined = sortBy ?? 'newest';

  const apiSortBy = normalizedSortBy === 'newest' ? 'publishedAt' : normalizedSortBy;

  const positionNumber = position ? Number(position) : undefined;

  const hasPosition = !!positionNumber && positionNumber >= 1;

  const currentPage = hasPosition ? Math.floor((positionNumber - 1) / POSTS_PER_PAGE) + 1 : 1;

  const indexOnPage = hasPosition ? (positionNumber - 1) % POSTS_PER_PAGE : 0;

  const pageParams: LibraryListParams = {
    limit: POSTS_PER_PAGE,
    cancerTypeId: effectiveCancerTypeId,
    // Same filter as the library list, which asks for the subtree.
    includeDescendants: true,
    q: effectiveSearchQuery,
    saved: effectiveShowSavedPosts,
    sortBy: apiSortBy,
  };

  const currentPageQuery = useLibraryPage(
    { ...pageParams, page: currentPage },
    { enabled: hasPosition }
  );

  const currentPagePosts = currentPageQuery.data?.posts || [];

  const previousIdOnPage = indexOnPage > 0 ? currentPagePosts[indexOnPage - 1]?.id : undefined;

  const nextIdOnPage =
    indexOnPage < currentPagePosts.length - 1 ? currentPagePosts[indexOnPage + 1]?.id : undefined;

  const needsPreviousPage =
    hasPosition && !previousIdOnPage && currentPage > 1 && currentPageQuery.isSuccess;

  const needsNextPage = hasPosition && !nextIdOnPage && currentPageQuery.isSuccess;

  const previousPageQuery = useLibraryPage(
    { ...pageParams, page: currentPage - 1 },
    { enabled: needsPreviousPage }
  );

  const nextPageQuery = useLibraryPage(
    { ...pageParams, page: currentPage + 1 },
    { enabled: needsNextPage }
  );

  const previousId =
    previousIdOnPage ??
    (needsPreviousPage ? previousPageQuery.data?.posts?.slice(-1)[0]?.id : undefined);

  const nextId = nextIdOnPage ?? (needsNextPage ? nextPageQuery.data?.posts?.[0]?.id : undefined);

  return useMemo(() => ({ previousId, nextId }), [previousId, nextId]);
};

type CommunityPostNavigationProps = {
  postId: number;
  setPostId: (postId: number) => void;
  position?: string;
};

/**
 * Moving to the previous or next post in the list the reader came from.
 *
 * Keeps the position and search context in the route, and moves the position by one with the
 * post, so the reader steps through the same list they opened it from.
 */
const usePostStepper = ({ postId, setPostId, position }: CommunityPostNavigationProps) => {
  const { previousId, nextId } = usePreviousAndNextPostIds(postId, position);

  const router = useRouter();

  const currentPosition = position ? Number(position) : undefined;

  const step = (targetId: number | undefined, offset: number) => {
    if (!targetId || !currentPosition) {
      return;
    }

    Promise.resolve(
      trackCommunityPostStepped({ direction: offset < 0 ? 'previous' : 'next' })
    ).catch(() => undefined);

    router.setParams({
      id: targetId.toString(),
      position: (currentPosition + offset).toString(),
    });

    setPostId(targetId);
  };

  return {
    hasPrevious: Boolean(previousId),
    hasNext: Boolean(nextId),
    goToPrevious: () => step(previousId, -1),
    goToNext: () => step(nextId, 1),
  };
};

/**
 * The web reading column: about 768px, centered, with the list's gutters. The header, the post,
 * the comments and the comment bar all share it so their left and right edges line up. Web only,
 * so a phone and native keep the layout they have.
 */
const READING_COLUMN_BOX = 'mx-auto w-full max-w-3xl';

const READING_COLUMN = `${READING_COLUMN_BOX} px-4 md:px-6`;

/** The post screen's frame around the shared content: its gutter and the rule above comments. */
const CommunityPostBody = ({ post, searchQuery }: { post: LibraryPost; searchQuery?: string }) => {
  if (!post) {
    return null;
  }

  if (isWeb) {
    // The rule above the comments stays full width; the content sits in the reading column.
    return (
      <View className="border-b border-b-outline-100">
        <CommunityPostContent
          post={post}
          searchQuery={searchQuery}
          className={twJoin(READING_COLUMN, 'pb-8 pt-10 md:pt-6')}
        />
      </View>
    );
  }

  return (
    <CommunityPostContent
      post={post}
      searchQuery={searchQuery}
      className="border-b border-b-outline-100 px-5 pb-8 pt-10 md:pt-6"
    />
  );
};

/**
 * The post's header, with previous and next in the trailing slot when the reader came from a
 * list. Each is its own item with its own background, so iOS 26 gives them separate Liquid
 * Glass capsules, the way the system separates actions that do different things; in one
 * capsule they read as a single control with two halves. On iOS they are straight arrows
 * rather than chevrons, so neither is mistaken for the system back button beside them.
 */
const CommunityPostHeader = ({
  postId,
  setPostId,
  position,
  title,
  contentScrollsUnderHeader = true,
}: {
  postId: number;
  setPostId: (postId: number) => void;
  position?: string;
  title?: string;
  contentScrollsUnderHeader?: boolean;
}) => {
  const navigation = useNavigation();

  const router = useRouter();

  const params = useLocalSearchParams<{
    id?: string;
    q?: string;
    position?: string;
    sortBy?: string;
    'cancer-type-id'?: string;
    'saved-posts'?: 'true';
  }>();

  const id = params.id as string;

  const q = params.q as string;

  const sortBy = params.sortBy as string | undefined;

  const cancerTypeIdParam = params['cancer-type-id'] as string | undefined;

  const savedPostsParam = params['saved-posts'] as 'true' | undefined;

  const { userId } = useUserScope();

  useEffect(() => {
    if (!userId || isWeb) {
      return;
    }

    const noPreviousHistory = navigation.getState()?.routes.length === 1;

    if (noPreviousHistory) {
      setTimeout(() => {
        router.replace({
          pathname: '/(secure)/(tabs)/community/' as never,
          params: { q: q || undefined },
        });

        setTimeout(() => {
          router.navigate({
            pathname: `/(secure)/(tabs)/(community)/community/${id}` as never,
            params: {
              q: q || undefined,
              ...(sortBy ? { sortBy } : {}),
              ...(cancerTypeIdParam ? { 'cancer-type-id': cancerTypeIdParam } : {}),
              ...(savedPostsParam ? { 'saved-posts': savedPostsParam } : {}),
              ...(position ? { position } : {}),
            },
          });
        }, 500);
      }, 0);
    }
  }, [userId, q]);

  const { hasPrevious, hasNext, goToPrevious, goToNext } = usePostStepper({
    postId,
    setPostId,
    position,
  });

  const stepItems: HeaderItem[] = [
    {
      type: 'button',
      key: 'previous',
      label: 'Previous inquiry',
      icon: { sf: 'arrow.left', lucide: ChevronLeft },
      isDisabled: !hasPrevious,
      isHidden: !position,
      separateBackground: true,
      onPress: goToPrevious,
    },
    {
      type: 'button',
      key: 'next',
      label: 'Next inquiry',
      icon: { sf: 'arrow.right', lucide: ChevronRight },
      isDisabled: !hasNext,
      isHidden: !position,
      separateBackground: true,
      onPress: goToNext,
    },
  ];

  const header = (
    <Header
      title={isWeb ? undefined : title}
      isBackButton
      backRoute={ROUTES.COMMUNITY}
      backRouteParams={{ q: q || undefined }}
      contentScrollsUnderHeader={contentScrollsUnderHeader}
      rightItems={stepItems}
    />
  );

  // On web the bar sits in the reading column, so Back is on the column's left edge and the
  // arrows on its right. The bar's own gutters are the column's gutters.
  return isWeb ? <View className={READING_COLUMN_BOX}>{header}</View> : header;
};

type ReplyToInfo = {
  commentId: number;
  user: CommentAuthor;
  isAnonymous: boolean;
};

/**
 * Community post detail view with position-based navigation.
 *
 * Navigation requires position parameter from URL to work. Without position,
 * prev/next buttons are disabled. Uses same search parameters as original list
 * to maintain consistent navigation context.
 *
 * URL params: id, position, q, sortBy, cancer-type-id, saved-posts
 */
export function CommunityPost() {
  const { id, scrollTo, q, position } = useLocalSearchParams<{
    id: string;
    scrollTo?: string;
    q?: string;
    position?: string;
  }>();

  const [postId, setPostId] = useState(Number(id));

  const [openedVia, setOpenedVia] = useState<CommunityPostOpenedVia>(position ? 'list' : 'direct');

  const showAdjacentPost = useCallback((adjacentPostId: number) => {
    setOpenedVia('pager');

    setPostId(adjacentPostId);
  }, []);

  const headerHeight = useHeaderHeight();

  /**
   * The keyboard offset is the distance from the top of the screen to this view. Under an
   * opaque bar that is the bar's height; a floating bar takes no room, so it is nothing.
   */
  const isHeaderFloating = useIsHeaderFloating();

  const cancerTypeId = useLibraryFilters((state) => state.cancerTypeId);

  const scrollViewRef = useRef<ScrollView>(null);

  const reducedMotion = useReducedMotion();

  const [commentSectionY, setCommentSectionY] = useState<number | null>(null);

  /** The comments section's element, set once it mounts. The web scroll reads its live position. */
  const [commentSectionNode, setCommentSectionNode] = useState<unknown>(null);

  const [hasScrolled, setHasScrolled] = useState(false);

  const [globalReplyToInfo, setGlobalReplyToInfo] = useState<ReplyToInfo | null>(null);

  const commentBoxRef = useRef<CommentBoxHandle>(null);

  const handleCommentSectionLayout = (event: LayoutChangeEvent) => {
    const { y } = event.nativeEvent.layout;

    readTracking.onAnswerEnd(y);

    requestAnimationFrame(() => {
      setCommentSectionY(y);
    });
  };

  const [initialScrollAttempts, setInitialScrollAttempts] = useState(0);

  const MAX_INITIAL_SCROLL_ATTEMPTS = 1;

  useEffect(() => {
    if (scrollTo === 'comments') {
      setHasScrolled(false);

      setInitialScrollAttempts(0);
    }
  }, [scrollTo]);

  /**
   * Web: scroll the section into view every time the request arrives, then clear it so the next
   * press (the same value again) is a change. Waits for the section to mount and keeps the
   * request until it has been delivered.
   */
  useEffect(() => {
    if (!isWeb || scrollTo !== 'comments' || !commentSectionNode) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (scrollCommentsIntoView(commentSectionNode as never, reducedMotion)) {
        router.setParams({ scrollTo: undefined });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [scrollTo, commentSectionNode, reducedMotion]);

  useEffect(() => {
    const nativeTargetY = nativeCommentsScrollY(commentSectionY);

    const shouldAttemptScroll =
      !isWeb &&
      scrollTo === 'comments' &&
      nativeTargetY !== null &&
      scrollViewRef.current &&
      !hasScrolled &&
      initialScrollAttempts < MAX_INITIAL_SCROLL_ATTEMPTS;

    if (!shouldAttemptScroll) {
      return;
    }

    const scrollToComments = () => {
      const ref = scrollViewRef.current;

      if (!ref) {
        return;
      }

      ref.scrollTo({ y: nativeTargetY ?? 0, animated: !reducedMotion });

      if (!hasScrolled && initialScrollAttempts < MAX_INITIAL_SCROLL_ATTEMPTS - 1) {
        setInitialScrollAttempts((prev) => prev + 1);

        return;
      }

      setHasScrolled(true);

      router.setParams({ scrollTo: undefined });
    };

    const delay = isWeb ? 100 : 500;

    const timeoutId = setTimeout(() => {
      requestAnimationFrame(scrollToComments);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [scrollTo, commentSectionY, hasScrolled, initialScrollAttempts, isWeb, reducedMotion]);

  useEffect(() => {
    if (Number(id) !== postId) {
      router.setParams({
        id: postId.toString(),
        ...(q ? { q } : {}),
      });
    }
  }, [id, postId, q]);

  const postsQuery = useLibraryPost({ postId, q: q || undefined });

  /**
   * The search and sort of the list the reader came from, read as the comment
   * box read them, so the reactions drawn in it patch that list.
   */
  const { q: reactionsSearch, sortBy: reactionsSortBy } = useGlobalSearchParams<{
    q?: string;
    sortBy?: string;
  }>();

  const refreshPostAfterComment = useRefreshPostAfterComment();

  const isInquirySummaryEnabled = useFeatureFlag(FEATURE_FLAGS.INQUIRY_SUMMARY);

  /** Signed out, the post is a preview, so its placeholder is short too. */
  const { userId } = useUserScope();

  /**
   * What the list already knew about this post, read while it loads so the
   * skeleton can match it: a summary card only if there is one, and as many
   * comment rows as it has. Undefined for a deep link.
   */
  const listedPost = useListedLibraryPost(postId, { enabled: !postsQuery.data });

  /** Known from the list the reader came from before the post itself loads. */
  const postTitle = postsQuery.data?.title ?? listedPost?.title;

  const toast = useToaster();

  /**
   * List position is reported only for a post opened from the list. Paging
   * moves the position param a beat after the post, so it would be off by one.
   */
  useFocusEffect(
    useCallback(() => {
      trackCommunityPostViewed({
        postId,
        openedVia,
        listPosition: openedVia === 'list' ? Number(position) || undefined : undefined,
      });
    }, [postId, openedVia])
  );

  const isInquiryLibraryAnswersEnabled = useFeatureFlag(FEATURE_FLAGS.INQUIRY_LIBRARY_ANSWERS);

  /** The same decision the post body makes, so a read reports the layout the reader saw. */
  const postFormat = useMemo(
    () =>
      postsQuery.data
        ? postFormatOf(
            isInquiryLibraryAnswersEnabled ? readInquiryLibraryAnswers(postsQuery.data) : null
          )
        : undefined,
    [postsQuery.data, isInquiryLibraryAnswersEnabled]
  );

  const readTracking = useCommunityPostReadTracking({
    postId,
    isReady: postsQuery.data !== undefined,
    openedVia,
    format: postFormat,
  });

  /**
   * Waits for the post, which carries the comments with it and seeds this
   * query's cache. Starting in parallel would spend a second read on the same
   * comments. Once the post is in, a fresh seed means no fetch at all; a stale
   * or missing one fetches as before.
   */
  const { commentsQuery, readCommentsUpdatedAt } = useComments(postId, {
    /**
     * Keyed on data rather than `isSuccess`: a failed background refresh
     * flips the status to error while the post stays on screen, and the
     * comments under it still need to load.
     */
    enabled: postsQuery.data !== undefined,
  });

  /**
   * One read refreshes both, since the post brings its comments. Falls back to
   * fetching comments on their own when the refreshed post did not carry them,
   * so a pull still refreshes comments against an API that predates it.
   */
  const refreshPostAndComments = async () => {
    const refreshStartedAt = Date.now();

    await postsQuery.refetch();

    const commentsUpdatedAt = readCommentsUpdatedAt();

    if (commentsUpdatedAt < refreshStartedAt) {
      await commentsQuery.refetch();
    }
  };

  const createCommentMutation = useCreateComment({
    onPosted: (variables) => {
      refreshPostAfterComment(variables.postId);

      setTimeout(() => {
        if (scrollViewRef.current) {
          scrollViewRef.current.scrollToEnd({ animated: true });
        }
      });
    },
  });

  const handleReply = (comment: AnyComment) => {
    const commentIsAnonymous = getCommentIsAnonymous(comment);

    setGlobalReplyToInfo({
      commentId: comment.id,
      user: comment.user,
      isAnonymous: commentIsAnonymous,
    });

    commentBoxRef.current?.focus();
  };

  /**
   * The full-screen failure is only for a post that never loaded. A refresh that
   * fails while the post is already on screen keeps the post and says so above
   * it, rather than taking away what the reader was reading.
   */
  if (postsQuery.isError && !postsQuery.data) {
    return (
      <CommunityWrapper>
        <VStack className={twJoin('w-full', !isWeb && 'flex-1')}>
          <CommunityPostHeader
            postId={postId}
            setPostId={showAdjacentPost}
            position={position}
            title={postTitle}
          />
          {/* A scroll view like the loaded post's, so the notice centres in the
              same space and the comment bar sits where it always does. */}
          <ScrollView
            contentInsetAdjustmentBehavior="automatic"
            showsVerticalScrollIndicator={false}
            contentContainerClassName="flex min-h-full">
            <LibraryRetryNotice
              error={postsQuery.error}
              failedAt={postsQuery.errorUpdatedAt}
              onRetry={() => postsQuery.refetch()}
              onBack={() => router.replace(ROUTES.COMMUNITY)}
              surface="post"
            />
          </ScrollView>
          <CommentBox key={postId} disabled />
        </VStack>
      </CommunityWrapper>
    );
  }

  if (!postsQuery.data) {
    return (
      <CommunityWrapper>
        <VStack className={twJoin('w-full', !isWeb && 'flex-1')}>
          <CommunityPostHeader
            postId={postId}
            setPostId={showAdjacentPost}
            position={position}
            title={postTitle}
            contentScrollsUnderHeader={false}
          />
          <PostDetailSkeleton
            hasSummary={isInquirySummaryEnabled && !!listedPost?.summary}
            commentCount={listedPost?.comments}
            isPreview={!userId}
          />
          <CommentBox key={postId} disabled />
        </VStack>
      </CommunityWrapper>
    );
  }

  const mainContent = (
    <VStack className={twJoin('w-full', !isWeb && 'flex-1')}>
      <CommunityPostHeader
        postId={postId}
        setPostId={showAdjacentPost}
        position={position}
        title={postTitle}
      />
      <ScrollView
        ref={scrollViewRef}
        contentInsetAdjustmentBehavior="automatic"
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={250}
        onScroll={readTracking.onScroll}
        onLayout={readTracking.onLayout}
        onTouchStart={() => {
          readTracking.onTouchStart();

          // A touch on the post blurs the comment field, so the reader can click out of it. It
          // also drops the field back to its resting bar, with the reactions in place.
          Keyboard.dismiss();
        }}
        keyboardShouldPersistTaps="handled"
        contentContainerClassName="flex min-h-full"
        refreshControl={
          <RefreshControl
            refreshing={postsQuery.isRefetching || commentsQuery.isRefetching}
            onRefresh={refreshPostAndComments}
          />
        }>
        {postsQuery.isError ? (
          <LibraryRetryNotice
            error={postsQuery.error}
            failedAt={postsQuery.errorUpdatedAt}
            onRetry={refreshPostAndComments}
            surface="post"
            variant="inline"
          />
        ) : null}
        <CommunityPostBody post={postsQuery.data} searchQuery={q} />
        <View
          ref={setCommentSectionNode as never}
          className="flex-grow scroll-mt-5"
          onLayout={handleCommentSectionLayout}>
          <Comments
            comments={commentsQuery.data}
            post={postsQuery.data}
            isLoading={commentsQuery.isLoading}
            isError={commentsQuery.isError}
            onReply={handleReply}
            onCommentPosted={refreshPostAfterComment}
          />
        </View>
      </ScrollView>
      {/* Keyed on the post, so a comment started on one post does not carry
          over to the next when Prev or Next swaps the post under it. The same
          box is drawn, without post data, while the post loads or waits. */}
      <CommentBox
        key={postId}
        ref={commentBoxRef}
        data={postsQuery.data}
        commentsLink={
          <CommunityActionBar
            item={postsQuery.data}
            only="comments"
            onPostPage
            searchInput={(reactionsSearch as string) || ''}
            sortBy={(reactionsSortBy as SortOption) || 'newest'}
            cancerTypeId={Number(cancerTypeId) || 0}
          />
        }
        reactions={
          <CommunityActionBar
            item={postsQuery.data}
            only="reactions"
            onPostPage
            searchInput={(reactionsSearch as string) || ''}
            sortBy={(reactionsSortBy as SortOption) || 'newest'}
            cancerTypeId={Number(cancerTypeId) || 0}
          />
        }
        replyToUser={(() => {
          if (!globalReplyToInfo?.user) {
            return null;
          }

          if (globalReplyToInfo.isAnonymous) {
            return { firstName: '', lastName: '' };
          }

          return {
            firstName: globalReplyToInfo.user.firstName ?? '',
            lastName: globalReplyToInfo.user.lastName ?? '',
          };
        })()}
        onSubmit={async ({ body, isAnonymous }) => {
          try {
            trackCommentSubmitted({
              postId,
              isReply: !!globalReplyToInfo?.commentId,
              isAnonymous,
            });

            await createCommentMutation.mutateAsync({
              body,
              postId,
              replyToId: globalReplyToInfo?.commentId,
              parentId: globalReplyToInfo?.commentId,
              isAnonymous,
            });

            setGlobalReplyToInfo(null);

            return true;
          } catch {
            toast.showError({
              title: 'Failed to Submit Comment',
              message: "We couldn't submit your comment. Please try again.",
            });

            return false;
          }
        }}
        onCancel={() => setGlobalReplyToInfo(null)}
        isSubmitting={createCommentMutation.isPending}
      />
    </VStack>
  );

  return (
    <CommunityWrapper>
      {isWeb ? (
        mainContent
      ) : (
        <KeyboardAvoidingView
          behavior="padding"
          className="w-full flex-1"
          keyboardVerticalOffset={Platform.OS === 'ios' && !isHeaderFloating ? headerHeight : 0}>
          {mainContent}
        </KeyboardAvoidingView>
      )}
    </CommunityWrapper>
  );
}
